/**
 * Renders a live Minecraft prefix from its `prefix_json` text component (guide §2).
 *
 * Observed shapes: `["", {"text":"[","color":"dark_gray"}, {"text":"Event Star","color":"gold"}, ...]`
 * and `{"text":"", "extra":[...]}`.
 * Rules:
 * 1. `prefixRuns` flattens the component into styled runs; children inherit their
 *    parent's style, as in game (a leading `""` resets it).
 * 2. Minecraft color names map to the classic palette (`MC_COLORS`); `#RRGGBB` literals pass through.
 * 3. Runs render on a dark chip background (prefixes are designed for the game chat).
 * 4. `prefix_json` may be `null` or have no text → fall back to the `name` field.
 */

import { cn } from '../../utils/cn';
import { prefixRuns } from '../../utils/prefixText';
import { MC_COLORS } from '../../types/eventTeam';

interface PrefixBadgeProps {
  prefixJson: string | null;
  name: string;
  size?: 'sm' | 'md';
  className?: string;
}

function resolveColor(color: string | undefined): string {
  if (!color) return '#FFFFFF';
  return MC_COLORS[color] ?? color;
}

export const PrefixBadge = ({ prefixJson, name, size = 'sm', className }: PrefixBadgeProps) => {
  const chipClasses = cn(
    'inline-flex items-center rounded-md bg-[#17172a] border border-glass-200/15 font-mono whitespace-pre',
    size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-2.5 py-1',
    className,
  );

  const runs = prefixRuns(prefixJson);
  if (!runs) {
    return (
      <span className={chipClasses} title={name}>
        <code className="text-glass-300">{name}</code>
      </span>
    );
  }

  return (
    <span className={chipClasses} title={name}>
      {runs.map((r, i) => (
        <span
          key={i}
          style={{ color: resolveColor(r.color) }}
          className={cn(
            r.bold && 'font-bold',
            r.italic && 'italic',
            r.underlined && 'underline',
            r.strikethrough && 'line-through',
          )}
        >
          {r.text}
        </span>
      ))}
    </span>
  );
};
